import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { apiClient as api } from '../../api';

interface Question {
  id?: number;
  question_key: string;
  question_text: string;
  help_text: string;
  answer_type: 'BOOLEAN' | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'TEXT';
  answer_options: string[];
  is_required: boolean;
  auto_populate_from_hris: boolean;
  display_order: number;
  is_active: boolean;
  rule_name?: string;
}

interface QuestionsStepProps {
  tenantId?: number;
  onNext: (questions: Question[]) => void;
  onBack: () => void;
  initialQuestions?: Question[];
}

const QuestionsStep: React.FC<QuestionsStepProps> = ({
  tenantId,
  onNext,
  onBack,
  initialQuestions = []
}) => {
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    if (initialQuestions.length === 0) {
      loadQuestions();
    }
  }, []);

  // Helper function to show notifications
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/risk-scoping/questions', {
        params: { tenant_id: tenantId }
      });
      setQuestions(response.data);
    } catch (err: any) {
      showNotification('Failed to load questions: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addNewQuestion = () => {
    const newQuestion: Question = {
      question_key: '',
      question_text: '',
      help_text: '',
      answer_type: 'BOOLEAN',
      answer_options: [],
      is_required: true,
      auto_populate_from_hris: false,
      display_order: questions.length + 1,
      is_active: true,
      rule_name: ''
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index: number, field: keyof Question, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const deleteQuestion = async (index: number) => {
    const questionToDelete = questions[index];

    // If question has an ID, delete it from database
    if (questionToDelete.id) {
      try {
        await api.delete(`/api/risk-scoping/questions/${questionToDelete.id}`);
        showNotification('Question deleted successfully', 'success');
      } catch (err: any) {
        showNotification('Failed to delete question: ' + err.message, 'error');
        return; // Don't remove from UI if database delete failed
      }
    }

    // Remove from local state
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  const addAnswerOption = (questionIndex: number) => {
    const updated = [...questions];
    if (!updated[questionIndex].answer_options) {
      updated[questionIndex].answer_options = [];
    }
    updated[questionIndex].answer_options.push('');
    setQuestions(updated);
  };

  const updateAnswerOption = (questionIndex: number, optionIndex: number, value: string) => {
    const updated = [...questions];
    updated[questionIndex].answer_options[optionIndex] = value;
    setQuestions(updated);
  };

  const deleteAnswerOption = (questionIndex: number, optionIndex: number) => {
    const updated = [...questions];
    updated[questionIndex].answer_options.splice(optionIndex, 1);
    setQuestions(updated);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      // Save each question
      for (const question of questions) {
        // Clean up data before saving - trim whitespace from all string fields
        const cleanedQuestion = {
          ...question,
          question_key: question.question_key.trim(),
          question_text: question.question_text.trim(),
          help_text: question.help_text?.trim() || '',
          answer_options: question.answer_options?.map(opt => opt.trim()) || []
        };

        if (question.id) {
          // Update existing
          await api.put(`/api/risk-scoping/questions/${question.id}`, cleanedQuestion);
        } else {
          // Create new
          const response = await api.post('/api/risk-scoping/questions', {
            ...cleanedQuestion,
            tenant_id: tenantId
          });
          question.id = response.data.id;
        }
      }
      showNotification('Draft saved successfully!', 'success');
    } catch (err: any) {
      showNotification('Failed to save draft: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Validation
    for (const q of questions) {
      if (!q.question_key || !q.question_text) {
        showNotification('Please fill in all required fields (Question Key and Question Text)', 'warning');
        return;
      }
      if ((q.answer_type === 'SINGLE_SELECT' || q.answer_type === 'MULTI_SELECT') &&
          (!q.answer_options || q.answer_options.length === 0)) {
        showNotification(`Question "${q.question_text}" requires answer options`, 'warning');
        return;
      }
    }

    setSaving(true);
    try {
      await saveDraft();
      onNext(questions);
    } catch (error) {
      showNotification('Failed to proceed to next step', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading questions...</Box>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Define Scoping Questions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create questions that will determine which risks are applicable to each process.
          Aim for 5-15 questions that capture key process attributes.
        </Typography>
        <Alert severity="info" sx={{ mt: 2, bgcolor: '#e3f2fd' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: '#1976d2' }}>
            💡 How It Works
          </Typography>
          <Box component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 1 } }}>
            <li>
              <Typography variant="body2">
                <strong>Create questions</strong> that capture key process attributes (e.g., "Is the RAU responsible for business continuity planning?")
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Define rules</strong> in Step 2 that map answers to risk filtering actions (e.g., "IF Business Continuity = Yes, THEN exclude certain risks")
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Multiple rules</strong> can use the same question with different conditions and actions
              </Typography>
            </li>
          </Box>
          <Divider sx={{ my: 1.5, borderColor: '#bbdefb' }} />
          <Typography variant="caption" color="text.secondary" display="block">
            <strong>Tip:</strong> Aim for 5-15 targeted questions that cover key process characteristics like ownership, technology dependencies, compliance requirements, and third-party involvement.
          </Typography>
        </Alert>
      </Box>

      {questions.map((question, qIndex) => (
        <Card key={qIndex} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Question {qIndex + 1}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => deleteQuestion(qIndex)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <TextField
              fullWidth
              label="Question Key"
              placeholder="e.g., BC (Business Continuity)"
              value={question.question_key}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'question_key', e.target.value.toUpperCase())}
              sx={{ mb: 2 }}
              required
              helperText="Short identifier used to reference this question in rules"
            />

            <TextField
              fullWidth
              label="Rule Name (Optional)"
              placeholder="e.g., Business Continuity Rule"
              value={question.rule_name || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'rule_name', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Descriptive name that will appear when defining rules for this question"
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Question Text"
              placeholder="e.g., Which department owns this process?"
              value={question.question_text}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'question_text', e.target.value)}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Help Text"
              placeholder="Check your organizational hierarchy. If unclear, select the primary responsible team."
              value={question.help_text}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'help_text', e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              select
              fullWidth
              label="Answer Type"
              value={question.answer_type}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'answer_type', e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="BOOLEAN">Yes/No</MenuItem>
              <MenuItem value="SINGLE_SELECT">Single Select</MenuItem>
              <MenuItem value="MULTI_SELECT">Multi Select</MenuItem>
              <MenuItem value="TEXT">Text Input</MenuItem>
            </TextField>

            {(question.answer_type === 'SINGLE_SELECT' || question.answer_type === 'MULTI_SELECT') && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Answer Options:
                </Typography>
                {question.answer_options?.map((option, oIndex) => (
                  <Box key={oIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Option ${oIndex + 1}`}
                      value={option}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => updateAnswerOption(qIndex, oIndex, e.target.value)}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteAnswerOption(qIndex, oIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addAnswerOption(qIndex)}
                >
                  Add Option
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={question.is_required}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'is_required', e.target.checked)}
                  />
                }
                label="Required question"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={question.auto_populate_from_hris}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuestion(qIndex, 'auto_populate_from_hris', e.target.checked)}
                  />
                }
                label="Auto-populate from HRIS"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Alert severity="info" icon={false}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📋 How This Question Will Be Used in Rules:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                In the next step, you'll create rules based on this question's answer to exclude/include specific risks.
              </Typography>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="primary">
                  Example Rule Structure:
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  <strong>IF</strong> answer to "{question.question_text || 'this question'}"
                  {question.answer_type === 'BOOLEAN' && ' equals "True" or "False"'}
                  {question.answer_type === 'SINGLE_SELECT' && ` equals one of: [${question.answer_options?.join(', ') || 'your options'}]`}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  <strong>THEN</strong> exclude/include risks from the Risk Taxonomy
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  💡 You can create multiple rules for this question with different exclusions
                </Typography>
              </Box>
            </Alert>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addNewQuestion}
        sx={{ mb: 3 }}
      >
        Add Another Question
      </Button>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack} disabled>
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={saveDraft}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={questions.length === 0 || saving}
          >
            {saving ? 'Processing...' : 'Next: Define Rules →'}
          </Button>
        </Box>
      </Box>

      {/* Toast Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestionsStep;
